import { ClientOutputs } from '@botpress/client'
import { useMemo } from 'react'
import { match } from 'ts-pattern'
import { ChartDateShortcut, dateShortcutDetails } from '~/features/analytics'
import { useAnalyticsOverview } from '~/hooks'
import { formatters } from '~/utils'

type MetricsRecord = ClientOutputs['getBotAnalytics']['records'][number]

type MetricConfig<T extends string> = {
  extractValue: (record: MetricsRecord) => number
  dateShortcut?: ChartDateShortcut
  chartDataKey: string
  valueKey: T
}

type AnalyticsMetricReturn<T extends string> = {
  [K in T]: number
} & {
  delta: number
  deltaFormatted: string
  chartData: Array<Record<string, unknown>>
}

export const useAnalyticsMetric = <T extends string>(
  botId: string,
  workspaceId: string,
  config: MetricConfig<T>
): AnalyticsMetricReturn<T> => {
  const { range, rangeDelta, lastRangeStart, previousRangeStart, previousRangeEnd } = useMemo(() => {
    const range = config.dateShortcut || 'lastWeek'
    const rangeDelta = dateShortcutDetails[range].days // Number of days in the range
    // Get the current period (from today to dateDelta days ago)
    const lastRangeStart = new Date()
    lastRangeStart.setDate(lastRangeStart.getDate() - rangeDelta)
    if (range !== 'lastDay') {
      lastRangeStart.setHours(0, 0, 0, 0)
    }

    // Get the previous period
    const previousRangeStart = new Date()
    previousRangeStart.setDate(previousRangeStart.getDate() - rangeDelta * 2 - 1)
    if (range !== 'lastDay') {
      previousRangeStart.setHours(0, 0, 0, 0)
    }

    const previousRangeEnd = new Date()
    previousRangeEnd.setDate(previousRangeEnd.getDate() - rangeDelta - 1)
    if (range !== 'lastDay') {
      previousRangeEnd.setHours(23, 59, 59, 999)
    }

    return {
      range,
      rangeDelta,
      lastRangeStart,
      previousRangeStart,
      previousRangeEnd,
    }
  }, [config.dateShortcut])

  const {
    data: { records: lastRangeRecords },
  } = useAnalyticsOverview({ botId, workspaceId, startDate: lastRangeStart, endDate: new Date() })
  const {
    data: { records: previousRangeRecords },
  } = useAnalyticsOverview({ botId, workspaceId, startDate: previousRangeStart, endDate: previousRangeEnd })

  const { lastRangeValue, delta, deltaFormatted, chartData } = useMemo(() => {
    // Sum values for each period
    const lastRangeValues = lastRangeRecords.reduce((sum, record) => sum + config.extractValue(record), 0)
    const previousRangeValues = previousRangeRecords.reduce((sum, record) => sum + config.extractValue(record), 0)

    // Calculate period change
    const getDeltaString = (current: number, previous: number): { delta: number; deltaFormatted: string } => {
      const rangeLabel = match(config.dateShortcut)
        .with('lastDay', () => 'last 24 hours')
        .with('lastWeek', () => 'last 7 days')
        .with('lastMonth', () => 'last 30 days')
        .with('last3months', () => 'last 90 days')
        .with('last6months', () => 'last 6 months')
        .with('lastYear', () => 'last year')
        .otherwise(() => 'last 7 days')
      // Case 1: Both periods have zero values
      if (current === 0 && previous === 0) {
        return { delta: 0, deltaFormatted: 'No change' }
      }

      // Case 2: Previous period was zero, current period has value
      if (previous === 0 && current > 0) {
        return { delta: 100, deltaFormatted: `+100% from ${rangeLabel}` }
      }

      // Case 3: Previous period had value, current period is zero
      if (previous > 0 && current === 0) {
        return { delta: -100, deltaFormatted: `-100% from ${rangeLabel}` }
      }

      // Case 4: Both periods have values - calculate percentage change
      if (previous > 0) {
        const delta = ((current - previous) / previous) * 100
        const absValue = Math.abs(delta)

        if (delta > 0) {
          return {
            delta: absValue,
            deltaFormatted: `+${formatters.numberLocale(absValue, 0, 'fr-FR')}% from ${rangeLabel}`,
          }
        } else if (delta < 0) {
          return {
            delta: -absValue,
            deltaFormatted: `-${formatters.numberLocale(absValue, 0, 'fr-FR')}% from ${rangeLabel}`,
          }
        } else {
          return { delta: 0, deltaFormatted: 'No change' }
        }
      }

      // Fallback case (shouldn't reach here with valid data)
      return { delta: 0, deltaFormatted: 'No data available' }
    }

    const { delta, deltaFormatted } = getDeltaString(lastRangeValues, previousRangeValues)

    // Create daily chart data for both periods
    const getDataForRange = (startDate: Date, records: MetricsRecord[]) => {
      const data = []
      for (let i = 0; i < (range === 'lastDay' ? 24 : rangeDelta) + 1; i++) {
        const period = new Date(startDate)
        if (range === 'lastDay') {
          period.setHours(period.getHours() + i)
        } else {
          period.setDate(startDate.getDate() + i)
        }
        const periodDateString = period.toISOString().split('T')[0]
        const periodDateDateTimeString = period.toISOString().split(':')[0]

        const periodRecord = records.find((record) => {
          if (range === 'lastDay') {
            return record.startDateTimeUtc?.startsWith(periodDateDateTimeString!)
          }
          return record.startDateTimeUtc?.startsWith(periodDateString!)
        })

        data.push({
          day: i,
          date: period.toISOString(),
          value: config.extractValue(periodRecord || ({} as MetricsRecord)),
        })
      }
      return data
    }

    const lastRangeData = getDataForRange(lastRangeStart, lastRangeRecords)
    const previousRangeData = getDataForRange(previousRangeStart, previousRangeRecords)

    // Combine data for chart - each point has both current and previous range values
    const chartData = lastRangeData.map((currentDay, index) => ({
      endDateTimeUtc: currentDay.date,
      day: currentDay.day,
      [config.chartDataKey]: currentDay.value,
      [`${config.chartDataKey}-${range}`]: previousRangeData[index]?.value || 0,
    }))

    return {
      lastRangeValue: lastRangeValues,
      delta,
      deltaFormatted,
      chartData,
    }
  }, [lastRangeRecords, previousRangeRecords, lastRangeStart, previousRangeStart, config, range, rangeDelta])

  return {
    [config.valueKey]: lastRangeValue,
    delta,
    deltaFormatted,
    chartData,
  } as AnalyticsMetricReturn<T>
}
